import { useId } from 'react';
import {
  LABEL_DESTINATIONS,
  type LabelDestination,
  type LabelDestinationId,
} from '../labels/destinations.js';
import { chooseDymoPrinter, refreshDymoStatus, type DymoSnapshot } from '../labels/dymo-browser.js';
import {
  DYMO_CONNECT_DOWNLOAD_URL,
  DYMO_CONNECT_NAME,
  DYMO_SETUP_STEPS,
} from '../labels/dymo-setup.js';
import {
  PERMISSION_WAIT_MESSAGE,
  isLabelWriter,
  rollSummary,
  rollWarnings,
  unavailableReason,
} from '../labels/dymo-status.js';
import { useDymoStatus } from '../labels/useDymoStatus.js';
import { useDestinationAvailability } from '../labels/useLabelDestination.js';
import { Button } from './ui.js';

/**
 * The shared pieces of the label-printing interface: the destination picker and
 * the DYMO printer panel (the settings page), the note under the destination
 * (the Labeling page), and the DYMO Connect install note (both).
 */

/**
 * Chooses this browser's label destination. Every destination is listed; one
 * that cannot be used on this computer right now is shown with the reason and
 * cannot be chosen.
 */
export function LabelDestinationPicker({
  value,
  onChange,
}: {
  value: LabelDestinationId;
  onChange: (id: LabelDestinationId) => void;
}) {
  const name = useId();
  const availability = useDestinationAvailability();

  return (
    <fieldset className="space-y-2">
      <legend className="mb-2 text-sm font-medium text-body">Print labels to</legend>
      {LABEL_DESTINATIONS.map((destination) => {
        const { available, reason } = availability[destination.id];
        const selected = value === destination.id;
        return (
          <label
            key={destination.id}
            className={`flex items-start gap-3 rounded-md px-3 py-2.5 ring-1 ring-inset ${
              selected ? 'bg-brand-50 ring-brand-600' : 'ring-line'
            } ${available ? 'cursor-pointer hover:bg-sunken' : 'opacity-60'}`}
          >
            <input
              type="radio"
              name={name}
              value={destination.id}
              checked={selected}
              disabled={!available}
              onChange={() => onChange(destination.id)}
              className="mt-0.5 accent-brand-600"
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-strong">{destination.name}</span>
              <span className="block text-xs text-muted">{destination.summary}</span>
              {!available && reason && (
                <span className="mt-0.5 block text-xs text-warn-text">{reason}</span>
              )}
              {!available && selected && (
                <span className="mt-0.5 block text-xs text-warn-text">
                  Until it is available, labels from this browser go to sheet labels, 30 per sheet.
                </span>
              )}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

/**
 * The required note: what a computer needs before it can print to the DYMO
 * labeler, with the link to DYMO's installer and the setup steps. Shown on the
 * Labeling page and on the settings page (docs/labels.md has the longer form).
 */
export function DymoConnectNote() {
  return (
    <div className="space-y-1 text-xs text-muted" data-testid="dymo-connect-note">
      <p>
        Printing straight to the DYMO labeler needs{' '}
        <span className="font-medium text-body">{DYMO_CONNECT_NAME}</span> installed on this
        computer.{' '}
        <a
          href={DYMO_CONNECT_DOWNLOAD_URL}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-brand-600 underline underline-offset-2"
        >
          Download DYMO Connect from DYMO
        </a>
      </p>
      <ol className="list-decimal space-y-0.5 pl-5">
        {DYMO_SETUP_STEPS.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p>
        The first time, your browser may ask whether this site may connect to apps on this device;
        choose Allow. Every other printer keeps using the PDF.
      </p>
    </div>
  );
}

/** What DYMO Connect reports right now: the printer and its roll, or why it cannot be used. */
export function DymoStatusLine({ snapshot }: { snapshot: DymoSnapshot }) {
  const { status, checking } = snapshot;
  if (status === null && checking && snapshot.waitingForPermission) {
    return (
      <p className="text-xs text-warn-text" role="status" data-testid="dymo-status">
        {PERMISSION_WAIT_MESSAGE}
      </p>
    );
  }
  if (status === null) {
    return (
      <p className="text-xs text-muted" role="status" data-testid="dymo-status">
        {checking
          ? 'Checking for DYMO Connect on this computer… If your browser asks to connect to apps on this device, choose Allow.'
          : 'DYMO Connect has not been checked yet.'}
      </p>
    );
  }
  const reason = unavailableReason(status);
  if (reason !== null) {
    return (
      <p className="text-xs text-warn-text" role="status" data-testid="dymo-status">
        {reason}
      </p>
    );
  }
  const printer = status.printer;
  const model =
    printer?.modelName && printer.modelName !== printer.name ? ` (${printer.modelName})` : '';
  const roll = rollSummary(status.consumable);
  return (
    <div className="space-y-1" role="status" data-testid="dymo-status">
      <p className="text-xs text-ok-text">
        Ready: {printer?.name}
        {model}
        {roll ? ` — ${roll}` : ''}.
      </p>
      {rollWarnings(status.consumable).map((warning) => (
        <p key={warning} className="text-xs font-medium text-warn-text">
          {warning}
        </p>
      ))}
    </div>
  );
}

/**
 * The DYMO part of the settings page: what DYMO Connect reports, every printer
 * it lists (a LabelWriter can be chosen; a tape printer cannot print these
 * labels and says so), which one this browser uses, and "Check again".
 */
export function DymoPrinterPanel() {
  const name = useId();
  const snapshot = useDymoStatus(true);
  const { status, checking } = snapshot;
  const printers = status?.printers ?? [];
  const severalLabelWriters = printers.filter(isLabelWriter).length > 1;

  return (
    <section className="space-y-3" aria-label="DYMO label printer">
      <h2 className="text-sm font-medium text-body">DYMO label printer</h2>
      <DymoStatusLine snapshot={snapshot} />

      {printers.length > 0 && (
        <fieldset className="space-y-1.5">
          <legend className="mb-1 text-xs text-muted">
            Printers DYMO Connect lists on this computer
            {severalLabelWriters ? ' — choose the one this browser uses' : ''}
          </legend>
          {printers.map((printer) => {
            const usable = isLabelWriter(printer);
            const facts = [
              printer.modelName && printer.modelName !== printer.name ? printer.modelName : null,
              usable ? null : 'not a LabelWriter, cannot print these labels',
              printer.isConnected ? 'connected' : 'not connected',
              printer.isLocal ? 'on this computer' : 'shared or on the network',
            ].filter((part) => part !== null);
            return (
              <label
                key={printer.name}
                className={`flex items-start gap-3 rounded-md px-3 py-2 ring-1 ring-inset ring-line ${
                  usable ? 'cursor-pointer hover:bg-sunken' : 'opacity-60'
                }`}
              >
                <input
                  type="radio"
                  name={name}
                  value={printer.name}
                  checked={status?.printer?.name === printer.name}
                  disabled={!usable || checking}
                  onChange={() => void chooseDymoPrinter(printer.name)}
                  className="mt-0.5 accent-brand-600"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-strong">{printer.name}</span>
                  <span className="block text-xs text-muted">{facts.join(' · ')}</span>
                </span>
              </label>
            );
          })}
        </fieldset>
      )}

      <Button variant="secondary" onClick={() => void refreshDymoStatus()} disabled={checking}>
        {checking ? 'Checking…' : 'Check again'}
      </Button>
      <DymoConnectNote />
    </section>
  );
}

/**
 * What the person needs to know about the destination before pressing Print:
 * for a PDF, how to print it; for the DYMO printer, which printer and roll the
 * job will go to. Rendered once, on the Labeling page, under the destination.
 */
export function LabelPrintNote({
  destination,
  dymo,
}: {
  destination: LabelDestination;
  /** The shared DYMO status, when the destination is the DYMO printer. */
  dymo?: DymoSnapshot;
}) {
  if (destination.kind === 'dymo') {
    return (
      <div className="space-y-1" data-testid="label-print-note">
        <p className="text-xs text-muted">
          The whole list goes to the label printer as one job, with no print dialog.
        </p>
        {dymo && <DymoStatusLine snapshot={dymo} />}
      </div>
    );
  }
  return (
    <p className="text-xs text-muted" data-testid="label-print-note">
      {destination.id === 'roll-30336'
        ? 'The PDF has one page per label at the label’s exact size. In the print dialog choose the label printer and the 30336 (1 x 2-1/8 in) paper size, at 100% scale.'
        : 'The PDF is laid out for US Letter sheets of 30 labels. Print it at 100% scale (“Actual size”), never “Fit to page” — any scaling moves the labels off the stickers.'}
    </p>
  );
}
